/**
 * Fuzzy, punctuation/location-tolerant vendor name matching for the frontend
 * vendor picker (Assign Vendor grid, Quick Assign, Company search).
 *
 * OmniDrive's vendor list has many entries that are really the same vendor split
 * across several company records for different subsidiaries/ship-from locations —
 * e.g. "HOLLAND HOUSE" and "HOLLAND HOUSE - HO CHI MINH", or the same vendor typed
 * with slightly different punctuation ("HALLMART COLLECTIBLES INC" vs
 * "HALLMART COLLECTIBLES INC."). Plain substring search on ~90 vendors made it easy
 * to miss the one you wanted. This gives the search box some "elasticity": typing
 * "Twin Star" (or a near-miss like "Twinstart") matches "TWIN STAR INTERNATIONAL",
 * "Holland House" matches both Holland House entries, "Element" matches
 * "ELEMENTS INTERNATIONAL - HO CHI MIN", etc.
 *
 * This mirrors the backend's VendorNameMatcher (used for automated Quick Assign
 * resolution) so search behavior is consistent across the app. Kept dependency-free
 * on purpose — no fuzzy-search library needed for ~90 rows.
 */

const NON_ALPHANUMERIC = /[^a-z0-9]/g;

/** Maximum edit distance allowed, as a fraction of the shorter normalized string's length. */
const MAX_DISTANCE_RATIO = 0.25;

/** Absolute floor so even short names ("PRIMA") tolerate a 1-character typo. */
const MIN_DISTANCE_ALLOWANCE = 1;

/** Below this length, fuzzy matching is skipped — too short to compare safely. */
const MIN_LENGTH_FOR_FUZZY = 3;

/**
 * Lowercases and strips every non-alphanumeric character (spaces, punctuation,
 * hyphens). Deliberately does not try to strip trailing "- LOCATION" qualifiers —
 * real vendor names contain mid-word hyphens (e.g. "TOP-LINE FURNITURE") that a
 * naive suffix-strip would mutilate. Location qualifiers are always appended after
 * the base name, so a plain prefix comparison already handles that case correctly.
 */
export function normalizeVendorName(raw) {
  return (raw || '').toLowerCase().replace(NON_ALPHANUMERIC, '');
}

/** Standard Levenshtein edit distance between two strings. */
function levenshtein(a, b) {
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[a.length][b.length];
}

/**
 * True if one normalized string is a prefix of the other (in either direction), or
 * within a small edit distance of that prefix — enough elasticity to absorb a
 * missing/extra/transposed character without matching unrelated names.
 *
 * @param {string} a already-normalized string (e.g. the search query)
 * @param {string} b already-normalized string (e.g. a candidate vendor name)
 */
export function fuzzyPrefixMatches(a, b) {
  if (!a || !b) return a === b;

  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;

  if (longer.startsWith(shorter)) return true;
  if (shorter.length < MIN_LENGTH_FOR_FUZZY) return false;

  const len = shorter.length;
  const maxDistance = Math.max(MIN_DISTANCE_ALLOWANCE, Math.floor(len * MAX_DISTANCE_RATIO));
  const lowerWindow = Math.max(0, len - maxDistance);
  const upperWindow = Math.min(longer.length, len + maxDistance);

  for (let window = lowerWindow; window <= upperWindow; window++) {
    if (levenshtein(shorter, longer.substring(0, window)) <= maxDistance) return true;
  }
  return false;
}

/**
 * Filters a list of items by a search query against a vendor/company name field,
 * with the elasticity described above. Empty query returns the full list unfiltered
 * (and unsorted) so the picker isn't empty by default.
 *
 * @param {Array<object>} items
 * @param {string} query raw, user-typed search text
 * @param {(item: object) => string} getName extracts the name to match against
 * @returns {Array<object>} matching items, closest matches first
 */
export function filterByVendorName(items, query, getName) {
  const normalizedQuery = normalizeVendorName(query);
  if (!normalizedQuery) return items;

  return items
    .map((item) => {
      const normalizedName = normalizeVendorName(getName(item));
      if (!fuzzyPrefixMatches(normalizedQuery, normalizedName)) return null;
      // Rank exact prefixes above fuzzy ones so precise typing still feels precise.
      const rank = normalizedName.startsWith(normalizedQuery) ? 0 : 1;
      return { item, rank };
    })
    .filter(Boolean)
    .sort((x, y) => x.rank - y.rank)
    .map((x) => x.item);
}
